import type { Octave } from '../note/index.js'
import { toEventDuration } from './duration.js'
import type { EasyScoreDuration } from './duration.js'
import type { NoteEvent } from '../progression/event.js'
import type { NoteName } from '@tunasong/schemas'

export const parseEasyScoreEvent = (text: string, defaultOctave = 4): NoteEvent => {
  /**
   * A note event is on the form Note:Accidental:Octave/Duration[/r][~]
   *
   * e.g., C, C4/8, C5/q, C#4/16, B4/h/r (a half rest), C4/q~ (tied to the next note) and so on.
   *
   * A dotted rest is written as VexFlow's grammar has it, the dot after the type: `B4/h/r.`.
   */
  const tie = text.endsWith('~')
  const event = tie ? text.slice(0, -1) : text
  const [, durationPart = '', type = ''] = event.split('/')
  const rest = type.startsWith('r')
  const restDots = rest ? type.slice(1) : ''

  const duration = (`${durationPart}${restDots}` as EasyScoreDuration) || 'q'
  const note = event[0]
  const accidential = event[1] === '#' ? '#' : event[1] === 'b' ? 'b' : undefined
  /** We are using isNaN and NOT Number.isNaN to coherce the value here */
  const octave =
    (!isNaN(event[1] as never)
      ? Number(event[1])
      : event[1] !== '/' && !isNaN(event[2] as never)
        ? Number(event[2])
        : undefined) ?? defaultOctave

  return {
    type: 'note',
    note: `${note}${accidential || ''}` as NoteName,
    duration: toEventDuration(duration),
    octave: octave as Octave,
    ...(rest ? { rest } : {}),
    ...(tie ? { tie } : {}),
  }
}

/** events is a comma separated string */
export const parseEasyScoreEvents = (events: string): NoteEvent[] =>
  events
    .split(/[,;\ ]/)
    .filter(Boolean)
    .map(e => parseEasyScoreEvent(e.trim()))
